/**
 * V1091: 서비스 상품 관리 API
 */

import apiClient from "./client";
import type {
  SpecialtyProduct,
  ServiceItemDto,
  CreateFromCatalogRequest,
  CreateCustomServiceRequest,
  ServiceUsageDto,
  UpdateServiceRequest,
  SubItemRequest,
  ServiceSubItem,
} from "@/types/services";

/**
 * 마스터 카탈로그 API
 */
export const specialtyProductsApi = {
  /** 현재 조직의 진료과 기준 마스터 상품 목록 */
  getAll: async (): Promise<SpecialtyProduct[]> => {
    const response = await apiClient.get<SpecialtyProduct[]>(
      "/api/specialty-products",
    );
    return response.data;
  },
};

/**
 * 서비스 상품 API
 */
export const servicesApi = {
  /** 현재 조직의 등록된 상품 목록 (서브아이템 포함) */
  getMyServices: async (): Promise<ServiceItemDto[]> => {
    const response = await apiClient.get<ServiceItemDto[]>("/api/services/me");
    return response.data;
  },

  /** 카탈로그에서 상품 등록 */
  createFromCatalog: async (
    data: CreateFromCatalogRequest,
  ): Promise<ServiceItemDto> => {
    const response = await apiClient.post<ServiceItemDto>(
      "/api/services/from-catalog",
      data,
    );
    return response.data;
  },

  /**
   * 커스텀 상품 직접 등록 (마스터 카탈로그 없이). V1254
   * 권한: ORG_ADMIN+ (백엔드 @PreAuthorize, 403 시 client.ts 인터셉터 처리)
   */
  createCustom: async (
    data: CreateCustomServiceRequest,
  ): Promise<ServiceItemDto> => {
    const response = await apiClient.post<ServiceItemDto>(
      "/api/services/custom",
      data,
    );
    return response.data;
  },

  /** 상품 삭제 영향 정량 조회 (예약/서브아이템 참조 카운트). V1254 */
  getUsage: async (id: number): Promise<ServiceUsageDto> => {
    const response = await apiClient.get<ServiceUsageDto>(
      `/api/services/${id}/usage`,
    );
    return response.data;
  },

  /**
   * 상품 표시 순서 일괄 변경 (드래그앤드롭). V1254 (3-2)
   * 권한: ORG_ADMIN+ (백엔드 @PreAuthorize). 타 조직 상품 포함 시 전체 거부(403).
   * displayOrder 는 0-based.
   */
  reorder: async (
    items: { serviceId: number; displayOrder: number }[],
  ): Promise<{ updatedCount: number }> => {
    const response = await apiClient.patch<{ updatedCount: number }>(
      "/api/services/reorder",
      items,
    );
    return response.data;
  },

  /** 상품 설정 업데이트 */
  update: async (
    id: number,
    data: UpdateServiceRequest,
  ): Promise<ServiceItemDto> => {
    const response = await apiClient.put<ServiceItemDto>(
      `/api/services/${id}`,
      data,
    );
    return response.data;
  },

  /** 노출/미노출 토글 */
  toggleVisibility: async (
    id: number,
    isVisible: boolean,
  ): Promise<ServiceItemDto> => {
    const response = await apiClient.patch<ServiceItemDto>(
      `/api/services/${id}/visibility`,
      { isVisible },
    );
    return response.data;
  },

  /** 상품 삭제 (soft delete - 비활성화) */
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/services/${id}`);
  },

  /** 상품 설정을 하위 서브아이템에 일괄 적용 */
  applyToSubItems: async (
    id: number,
    applyFields: string[],
  ): Promise<{ updatedCount: number; appliedFields: string[] }> => {
    const response = await apiClient.patch(
      `/api/services/${id}/apply-to-sub-items`,
      { applyFields },
    );
    return response.data;
  },

  // ===== 서브아이템 =====

  /** 서브아이템 추가 */
  createSubItem: async (
    serviceId: number,
    data: SubItemRequest,
  ): Promise<ServiceSubItem> => {
    const response = await apiClient.post<ServiceSubItem>(
      `/api/services/${serviceId}/sub-items`,
      data,
    );
    return response.data;
  },

  /** 서브아이템 수정 */
  updateSubItem: async (
    serviceId: number,
    subItemId: number,
    data: SubItemRequest,
  ): Promise<ServiceSubItem> => {
    const response = await apiClient.put<ServiceSubItem>(
      `/api/services/${serviceId}/sub-items/${subItemId}`,
      data,
    );
    return response.data;
  },

  /** 서브아이템 삭제 */
  deleteSubItem: async (
    serviceId: number,
    subItemId: number,
  ): Promise<void> => {
    await apiClient.delete(`/api/services/${serviceId}/sub-items/${subItemId}`);
  },
};
